/**
 * DoorlyMarket Due Diligence Room — investor-facing.
 *
 * Three-pane layout:
 *   Left:  Document navigator (Pool Overview, Loan Tape, per-loan files)
 *   Main:  Active panel content (charts, tape preview, per-loan documents)
 *   Header: Pool name + days-remaining countdown
 *
 * All data fetched via /api/market/bids/:bidId/dd-room/* — backend
 * enforces dd_open status and PII redaction on every endpoint.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, CartesianGrid, Tooltip } from 'recharts';
import { useInvestorAuth } from '../../context/InvestorAuthContext';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const DOC_TYPE_LABELS = {
  appraisal:           'Appraisal Report',
  closing_disclosure:  'Closing Disclosure',
  hoi_declaration:     'HOI Declaration',
  initial_disclosures: 'Initial Disclosures',
  promissory_note:     'Promissory Note',
  deed_of_trust:       'Deed of Trust',
  title_commitment:    'Title Commitment',
  borrower_psa_signed: 'Borrower Purchase Agreement',
  psa_ab_signed:       'Executed Purchase Agreement',
};

function fmt$(n) {
  const v = Number(n) || 0;
  return v ? `$${Math.round(v).toLocaleString()}` : '—';
}
function fmtUpb(n) {
  const v = Number(n) || 0;
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(2)}M`;
  if (v >= 1_000)     return `$${(v / 1_000).toFixed(0)}K`;
  return v ? `$${v.toLocaleString()}` : '—';
}
function fmtDate(d) {
  if (!d) return '—';
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return '—';
  return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}
function topNFromObj(obj, n = 6) {
  if (!obj || typeof obj !== 'object') return [];
  return Object.entries(obj)
    .map(([name, value]) => ({ name, value: Number(value) || 0 }))
    .sort((a, b) => b.value - a.value)
    .slice(0, n);
}

export default function MarketDDRoom() {
  const { bidId } = useParams();
  const navigate = useNavigate();
  const { token, investor, investorLogout } = useInvestorAuth();
  const [manifest, setManifest] = useState(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [activePanel, setActivePanel] = useState('overview');
  const [activeLoanId, setActiveLoanId] = useState(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/market/bids/${bidId}/dd-room/manifest`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!mounted) return;
        setManifest(res.data);
      } catch (err) {
        if (err.response?.status === 403) {
          alert('DD room access denied or not yet open.');
          navigate('/market/bids', { replace: true });
          return;
        }
        if (mounted) setError(err.response?.data?.error || 'Failed to load DD room.');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [bidId, token, navigate]);

  if (loading) {
    return <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>Loading DD Room…</div>;
  }
  if (error || !manifest) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#f9fafb', padding: 24 }}>
        <div style={{ maxWidth: 720, margin: '40px auto', backgroundColor: '#fff', border: '1px solid #ffd6db', borderRadius: 14, padding: 28, textAlign: 'center', color: '#ff082f' }}>
          {error || 'DD room not available.'}
        </div>
      </div>
    );
  }

  const { pool, documents, daysRemaining } = manifest;
  const countdownColor = daysRemaining < 7 ? '#ff082f' : daysRemaining < 14 ? '#ff082f' : '#acf44e';

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f9fafb', display: 'flex', flexDirection: 'column' }}>
      {/* Top header bar — dark, full width */}
      <header style={{
        backgroundColor: '#111827',
        color: '#fff',
        padding: '14px 24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 12,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 18 }}>
          <Link to="/market/bids" style={{ color: '#9ca3af', textDecoration: 'none', fontSize: 13 }}>
            ← Back to My Bids
          </Link>
          <div style={{ fontSize: 15, fontWeight: 700 }}>
            Due Diligence Room — {pool.poolName} ({pool.poolNumber})
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: countdownColor }}>
            {daysRemaining} day{daysRemaining === 1 ? '' : 's'} remaining
          </span>
          <span style={{ fontSize: 13, color: '#9ca3af' }}>
            {investor?.firstName} {investor?.lastName}
          </span>
          <button
            onClick={investorLogout}
            style={{ backgroundColor: 'transparent', border: '1px solid #374151', borderRadius: 8, padding: '6px 12px', fontSize: 13, cursor: 'pointer', color: '#e5e7eb' }}
          >
            Sign out
          </button>
        </div>
      </header>

      {/* Body — sidebar + main */}
      <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
        {/* Left sidebar */}
        <aside style={{
          width: 280,
          backgroundColor: '#fff',
          borderRight: '1px solid #e5e7eb',
          padding: '18px 0',
          overflowY: 'auto',
          flexShrink: 0,
        }}>
          <div style={{ padding: '0 18px 12px', fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.6 }}>
            Documents
          </div>
          <NavItem
            active={activePanel === 'overview'}
            label="📊 Pool Overview"
            onClick={() => { setActivePanel('overview'); setActiveLoanId(null); }}
          />
          <NavItem
            active={activePanel === 'loan_tape'}
            label="📋 Loan Tape"
            onClick={() => { setActivePanel('loan_tape'); setActiveLoanId(null); }}
            badge={documents.loanTape?.loanCount}
          />
          <div style={{ padding: '14px 18px 6px', fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.6 }}>
            📁 Loan Files
          </div>
          {documents.loanFiles.map((l) => (
            <NavItem
              key={l.loanId}
              active={activePanel === 'loan_file' && activeLoanId === l.loanId}
              indent
              label={
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 6 }}>
                  <span>{l.loanNumber}</span>
                  <span style={{ fontSize: 11, color: '#9ca3af' }}>
                    {l.state || '—'} · {fmtUpb(l.upb)}
                  </span>
                </div>
              }
              dim={!l.available}
              onClick={() => { setActivePanel('loan_file'); setActiveLoanId(l.loanId); }}
            />
          ))}
        </aside>

        {/* Main content */}
        <main style={{ flex: 1, padding: '24px 28px', overflowY: 'auto' }}>
          {activePanel === 'overview' && <OverviewPanel bidId={bidId} token={token} pool={pool} />}
          {activePanel === 'loan_tape' && <LoanTapePanel bidId={bidId} token={token} pool={pool} />}
          {activePanel === 'loan_file' && activeLoanId && (
            <LoanFilePanel
              bidId={bidId}
              loanId={activeLoanId}
              token={token}
              loanMeta={documents.loanFiles.find((l) => l.loanId === activeLoanId)}
            />
          )}

          {/* Confidentiality footer */}
          <div style={{
            marginTop: 32,
            padding: '14px 18px',
            backgroundColor: '#f9fafb',
            border: '1px solid #e5e7eb',
            borderRadius: 10,
            fontSize: 12,
            color: '#6b7280',
            lineHeight: 1.6,
          }}>
            All information in this Due Diligence Room is strictly confidential
            and subject to the Non-Disclosure Agreement signed on{' '}
            {fmtDate(investor?.ndaSignedAt || investor?.nda_signed_at)}.
            Unauthorized disclosure is prohibited.
          </div>
        </main>
      </div>
    </div>
  );
}

function NavItem({ active, label, badge, indent, dim, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        width: '100%',
        textAlign: 'left',
        padding: indent ? '8px 18px 8px 36px' : '10px 18px',
        fontSize: 13,
        fontWeight: active ? 700 : 500,
        color: active ? '#3a6ad9' : (dim ? '#9ca3af' : '#374151'),
        backgroundColor: active ? '#eff3fe' : 'transparent',
        border: 'none',
        borderLeft: active ? '3px solid #3a6ad9' : '3px solid transparent',
        cursor: 'pointer',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 8,
      }}
    >
      <span style={{ flex: 1, minWidth: 0 }}>{label}</span>
      {badge != null && (
        <span style={{ fontSize: 11, color: '#6b7280' }}>{badge}</span>
      )}
    </button>
  );
}

function OverviewPanel({ bidId, token, pool }) {
  const [strat, setStrat] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let mounted = true;
    axios.get(`${API_URL}/api/market/bids/${bidId}/dd-room/stratification`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => { if (mounted) setStrat(r.data); })
      .catch(() => {})
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [bidId, token]);

  return (
    <div>
      <h1 style={{ fontSize: 20, fontWeight: 800, color: '#111827', margin: 0 }}>Pool Overview</h1>
      <p style={{ fontSize: 13, color: '#6b7280', margin: '4px 0 18px' }}>
        Aggregate metrics and stratification for {pool.poolName}.
      </p>

      <div style={card}>
        <div style={cardTitle}>Summary</div>
        {loading ? (
          <div style={{ color: '#9ca3af', fontSize: 13 }}>Loading…</div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10 }}>
            <Stat label="Total UPB" value={fmtUpb(strat?.pool?.totalUpb || pool.totalUpb)} />
            <Stat label="Loan Count" value={strat?.pool?.loanCount ?? pool.loanCount ?? 0} />
            <Stat label="WA Note Rate" value={strat?.pool?.waNoteRate ? `${Number(strat.pool.waNoteRate).toFixed(2)}%` : '—'} />
            <Stat label="WA FICO" value={strat?.pool?.waFico ?? '—'} />
            <Stat label="WA LTV" value={strat?.pool?.waOriginalLtv ? `${Number(strat.pool.waOriginalLtv).toFixed(0)}%` : '—'} />
            <Stat label="Avg. Age" value={strat?.pool?.waLoanAgeMonths ? `${strat.pool.waLoanAgeMonths} mo` : '—'} />
          </div>
        )}
      </div>

      {strat?.stratification && (
        <div style={{ ...card, marginTop: 16 }}>
          <div style={cardTitle}>Stratification</div>
          <Strat label="Geographic Distribution" data={topNFromObj(strat.stratification.geographic, 6)} />
          <Strat label="Property Type"           data={topNFromObj(strat.stratification.propertyType, 6)} />
          <Strat label="Occupancy"               data={topNFromObj(strat.stratification.occupancy, 6)} />
          <Strat label="Doc Type"                data={topNFromObj(strat.stratification.docType, 6)} />
        </div>
      )}
    </div>
  );
}

function LoanTapePanel({ bidId, token, pool }) {
  const [tape, setTape] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let mounted = true;
    axios.get(`${API_URL}/api/market/bids/${bidId}/dd-room/loan-tape`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => { if (mounted) setTape(r.data); })
      .catch((err) => { if (mounted) setError(err.response?.data?.error || 'Failed.'); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [bidId, token]);

  const handleCsvDownload = async () => {
    try {
      const r = await axios.get(`${API_URL}/api/market/bids/${bidId}/dd-room/loan-tape?format=csv`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${pool.poolNumber || 'pool'}_loan_tape.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      alert(err.response?.data?.error || 'CSV download failed.');
    }
  };

  const aggregateUpb = useMemo(() => {
    if (!tape?.loans) return 0;
    return tape.loans.reduce((s, l) => s + (Number(l.current_upb) || 0), 0);
  }, [tape]);

  const payHistColor = (h) => {
    const n = Number(h);
    if (Number.isFinite(n)) {
      if (n === 0) return '#acf44e';
      if (n >= 3)  return '#ff082f';
      return '#ff082f';
    }
    if (typeof h === 'string') {
      if (/^0+$/.test(h)) return '#acf44e';
      if (/[3-9X]/.test(h)) return '#ff082f';
      return '#ff082f';
    }
    return '#9ca3af';
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 12, marginBottom: 14 }}>
        <div>
          <h1 style={{ fontSize: 20, fontWeight: 800, color: '#111827', margin: 0 }}>Loan Tape</h1>
          <p style={{ fontSize: 13, color: '#6b7280', margin: '4px 0 0' }}>
            {tape?.loans?.length || 0} loans · aggregate UPB {fmtUpb(aggregateUpb)}
          </p>
        </div>
        <button onClick={handleCsvDownload} style={btnPrimary}>
          Download Full Loan Tape (CSV)
        </button>
      </div>

      <div style={card}>
        {loading ? (
          <div style={{ color: '#9ca3af', fontSize: 13 }}>Loading tape…</div>
        ) : error ? (
          <div style={{ color: '#ff082f', fontSize: 13 }}>{error}</div>
        ) : !tape?.loans?.length ? (
          <div style={{ color: '#9ca3af', fontSize: 13 }}>No loans in this pool.</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ backgroundColor: '#f9fafb' }}>
                {['State','Type','Occ.','UPB','Rate','FICO','LTV','Age','Pay History'].map((h) => (
                  <th key={h} style={{ padding: '8px 10px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {tape.loans.slice(0, 20).map((l, i) => (
                <tr key={i} style={{ borderTop: '1px solid #f3f4f6' }}>
                  <td style={td}>{l.property_state || '—'}</td>
                  <td style={td}>{l.property_type || '—'}</td>
                  <td style={td}>{l.occupancy || '—'}</td>
                  <td style={td}>{fmt$(l.current_upb)}</td>
                  <td style={td}>{l.note_rate ? `${Number(l.note_rate).toFixed(2)}%` : '—'}</td>
                  <td style={td}>{l.original_fico || '—'}</td>
                  <td style={td}>{l.original_ltv ? `${l.original_ltv}%` : '—'}</td>
                  <td style={td}>{l.loan_age_months ?? '—'}</td>
                  <td style={td}>
                    <span style={{
                      display: 'inline-block', width: 10, height: 10, borderRadius: '50%',
                      backgroundColor: payHistColor(l.payment_history), marginRight: 6,
                    }} />
                    {l.payment_history || '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <div style={{ marginTop: 12, padding: '10px 14px', backgroundColor: '#ffe6e9', border: '1px solid #ffd6db', borderRadius: 10, fontSize: 12, color: '#ff082f' }}>
        This data is confidential and subject to your NDA.
      </div>
    </div>
  );
}

function LoanFilePanel({ bidId, loanId, token, loanMeta }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError('');
    axios.get(`${API_URL}/api/market/bids/${bidId}/dd-room/loan-files/${loanId}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => { if (mounted) setData(r.data); })
      .catch((err) => { if (mounted) setError(err.response?.data?.error || 'Failed.'); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [bidId, loanId, token]);

  const handleDownload = async (docId, originalName) => {
    try {
      const r = await axios.get(
        `${API_URL}/api/market/bids/${bidId}/dd-room/loan-files/${loanId}/download/${docId}`,
        { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' }
      );
      const url = URL.createObjectURL(r.data);
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      alert(err.response?.data?.error || 'Download failed.');
    }
  };

  const loan = data?.loan || loanMeta;
  return (
    <div>
      <h1 style={{ fontSize: 20, fontWeight: 800, color: '#111827', margin: 0 }}>
        Loan {loan?.loanNumber || ''}
      </h1>
      <p style={{ fontSize: 13, color: '#6b7280', margin: '4px 0 18px' }}>
        {loan?.state || '—'} · {loan?.propertyType || '—'} · {fmt$(loan?.upb)}
        {loan?.fico ? ` · FICO ${loan.fico}` : ''}
      </p>

      <div style={card}>
        <div style={cardTitle}>Available documents</div>
        {loading ? (
          <div style={{ color: '#9ca3af', fontSize: 13 }}>Loading…</div>
        ) : error ? (
          <div style={{ color: '#ff082f', fontSize: 13 }}>{error}</div>
        ) : !data?.documents?.length ? (
          <div style={{ color: '#9ca3af', fontSize: 13 }}>No documents available for this loan.</div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 12 }}>
            {data.documents.map((d) => (
              <div key={d.id} style={{ border: '1px solid #e5e7eb', borderRadius: 10, padding: 14, display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>
                  {DOC_TYPE_LABELS[d.documentType] || d.documentType}
                </div>
                <div style={{ fontSize: 11, color: '#6b7280' }}>{fmtDate(d.uploadDate)}</div>
                <button
                  onClick={() => handleDownload(d.id, d.originalName)}
                  style={{ ...btnPrimary, padding: '6px 12px', fontSize: 12 }}
                >
                  Download
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div style={{ backgroundColor: '#f9fafb', border: '1px solid #f3f4f6', borderRadius: 10, padding: 12 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 800, color: '#111827', marginTop: 2 }}>{value}</div>
    </div>
  );
}

function Strat({ label, data }) {
  if (!data || data.length === 0) return null;
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>{label}</div>
      <ResponsiveContainer width="100%" height={Math.max(140, data.length * 26)}>
        <BarChart layout="vertical" data={data} margin={{ left: 12, right: 16, top: 4, bottom: 4 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" horizontal={false} />
          <XAxis type="number" tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
          <YAxis dataKey="name" type="category" tick={{ fontSize: 11, fill: '#374151' }} axisLine={false} tickLine={false} width={80} />
          <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e5e7eb' }} />
          <Bar dataKey="value" fill="#3a6ad9" radius={[0, 4, 4, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

const card      = { backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: 14, padding: 20 };
const cardTitle = { fontSize: 14, fontWeight: 800, color: '#111827', marginBottom: 12 };
const btnPrimary = { fontSize: 13, fontWeight: 700, padding: '8px 16px', borderRadius: 8, border: 'none', backgroundColor: '#3a6ad9', color: '#fff', cursor: 'pointer' };
const td = { padding: '8px 10px', fontSize: 12, color: '#374151' };
